
import { useState } from 'react';
import Button from '../../../components/base/Button';

export default function ContactFormSection() {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    zipCode: '',
    projectType: '',
    budget: '',
    timeline: '',
    contactPreference: '',
    description: '',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus('idle');

    try {
      const formDataToSend = new FormData();
      Object.entries(formData).forEach(([key, value]) => {
        formDataToSend.append(key, value);
      });

      const response = await fetch('https://readdy.ai/api/form/d3kj4s5535piahg0gq70', {
        method: 'POST',
        body: formDataToSend,
      });

      if (response.ok) {
        setSubmitStatus('success');
        setFormData({
          name: '',
          email: '',
          phone: '',
          zipCode: '',
          projectType: '',
          budget: '',
          timeline: '',
          contactPreference: '',
          description: '',
        });
      } else {
        setSubmitStatus('error');
      }
    } catch (error) {
      console.error('Form submission error:', error);
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleInputChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>
  ) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  return (
    <section id="contractor-form" className="py-20 bg-blue-600">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-12">
          <div className="bg-green-500 text-white px-8 py-4 rounded-xl inline-block mb-6">
            <div className="flex items-center space-x-3">
              <i className="ri-lightbulb-line text-2xl"></i>
              <div>
                <div className="font-semibold text-xl">Smart Planning: Know before you spend</div>
                <div className="text-sm">
                  Get detailed costs, timeline, and options - even if you're just exploring
                </div>
              </div>
            </div>
          </div>

          <h2 className="text-3xl lg:text-4xl font-bold text-white mb-4">Get Your Free Project Quote</h2>
          <p className="text-xl text-blue-100 max-w-2xl mx-auto">
            Whether you're planning for next month or next year, get accurate pricing and expert insights to
            make informed decisions about your bathroom project.
          </p>

          <div className="bg-blue-500 border border-blue-400 rounded-xl p-4 mt-6 max-w-2xl mx-auto">
            <div className="flex items-center justify-center space-x-2 text-blue-100">
              <i className="ri-shield-check-line text-xl"></i>
              <span className="font-medium">Privacy Promise:</span>
              <span>Only 1-2 qualified contractors will contact you</span>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-2xl shadow-2xl p-8 lg:p-12">
          {/* Value‑Building Section (omitted for brevity) */}
          <form
            onSubmit={handleSubmit}
            className="space-y-6"
            data-readdy-form
            id="contractor-request-with-email"
          >
            <div className="grid md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">Full Name *</label>
                <input
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={handleInputChange}
                  required
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                  placeholder="Enter your full name"
                />
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">Email Address *</label>
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleInputChange}
                  required
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                  placeholder="Enter your email"
                />
              </div>
            </div>

            <div className="grid md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">Phone Number *</label>
                <input
                  type="tel"
                  name="phone"
                  value={formData.phone}
                  onChange={handleInputChange}
                  required
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                  placeholder="(555) 123-4567"
                />
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Connecticut Zip Code *
                </label>
                <input
                  type="text"
                  name="zipCode"
                  value={formData.zipCode}
                  onChange={handleInputChange}
                  required
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                  placeholder="06001"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                How Would You Prefer to Be Contacted? *
              </label>
              <p className="text-sm text-gray-600 mb-4">
                Only 1-2 pre‑screened contractors will reach out using your preferred method
              </p>
              <div className="grid md:grid-cols-2 gap-4">
                <div className="relative">
                  <input
                    type="radio"
                    name="contactPreference"
                    value="phone-call"
                    onChange={handleInputChange}
                    className="sr-only"
                    id="phone-call"
                  />
                  <label
                    htmlFor="phone-call"
                    className={`block p-4 border-2 rounded-lg cursor-pointer text-center font-medium transition-all ${
                      formData.contactPreference === 'phone-call'
                        ? 'border-blue-500 bg-blue-50 text-blue-700'
                        : 'border-gray-300 hover:border-blue-300'
                    }`}
                  >
                    <i className="ri-phone-line text-xl block mb-2"></i>
                    Phone Call
                    <div className="text-xs text-gray-600">Brief conversation about your project</div>
                  </label>
                </div>

                <div className="relative">
                  <input
                    type="radio"
                    name="contactPreference"
                    value="text-message"
                    onChange={handleInputChange}
                    className="sr-only"
                    id="text-message"
                  />
                  <label
                    htmlFor="text-message"
                    className={`block p-4 border-2 rounded-lg cursor-pointer text-center font-medium transition-all ${
                      formData.contactPreference === 'text-message'
                        ? 'border-green-500 bg-green-50 text-green-700'
                        : 'border-gray-300 hover:border-green-300'
                    }`}
                  >
                    <i className="ri-message-3-line text-xl block mb-2"></i>
                    Text Message
                    <div className="text-xs text-gray-600">Convenient messaging</div>
                  </label>
                </div>
              </div>
            </div>

            <div className="grid md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Project Type *
                </label>
                <select
                  name="projectType"
                  value={formData.projectType}
                  onChange={handleInputChange}
                  required
                  className="w-full px-4 py-3 pr-8 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                >
                  <option value="">Select project type</option>
                  <option value="full-remodel">Full Bathroom Remodel</option>
                  <option value="shower-install">Shower Installation</option>
                  <option value="tub-replacement">Bathtub Replacement</option>
                  <option value="tile-work">Tile Work</option>
                  <option value="plumbing">Plumbing Updates</option>
                  <option value="accessibility">Accessibility Upgrades</option>
                  <option value="other">Other</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Budget Range *
                </label>
                <select
                  name="budget"
                  value={formData.budget}
                  onChange={handleInputChange}
                  required
                  className="w-full px-4 py-3 pr-8 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                >
                  <option value="">Select budget range</option>
                  <option value="under-5k">Under $5,000</option>
                  <option value="5k-10k">$5,000 - $10,000</option>
                  <option value="10k-20k">$10,000 - $20,000</option>
                  <option value="20k-35k">$20,000 - $35,000</option>
                  <option value="35k-50k">$35,000 - $50,000</option>
                  <option value="over-50k">Over $50,000</option>
                </select>
              </div>
            </div>

            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                When Would You Like Your Quote? *
              </label>
              <p className="text-sm text-gray-600 mb-4">
                <i className="ri-lightbulb-line text-blue-500 mr-1"></i>
                Getting a quote early helps you budget properly and avoid contractor surprises - even if you're not
                ready to start yet
              </p>
              <div className="grid md:grid-cols-3 gap-4">
                <div className="relative">
                  <input
                    type="radio"
                    name="timeline"
                    value="immediately"
                    onChange={handleInputChange}
                    className="sr-only"
                    id="immediately"
                  />
                  <label
                    htmlFor="immediately"
                    className={`block p-4 border-2 rounded-lg cursor-pointer text-center font-medium transition-all ${
                      formData.timeline === 'immediately'
                        ? 'border-green-500 bg-green-50 text-green-700'
                        : 'border-gray-300 hover:border-green-300'
                    }`}
                  >
                    <i className="ri-calculator-line text-xl block mb-2"></i>
                    Quote Within 48hrs
                    <div className="text-xs text-green-600 font-semibold">Best deal: $1,750 off</div>
                    <div className="text-xs text-gray-500 mt-1">Know costs quickly</div>
                  </label>
                </div>

                <div className="relative">
                  <input
                    type="radio"
                    name="timeline"
                    value="within-1-month"
                    onChange={handleInputChange}
                    className="sr-only"
                    id="within-1-month"
                  />
                  <label
                    htmlFor="within-1-month"
                    className={`block p-4 border-2 rounded-lg cursor-pointer text-center font-medium transition-all ${
                      formData.timeline === 'within-1-month'
                        ? 'border-blue-500 bg-blue-50 text-blue-700'
                        : 'border-gray-300 hover:border-blue-300'
                    }`}
                  >
                    <i className="ri-calendar-line text-xl block mb-2"></i>
                    Quote This Week
                    <div className="text-xs text-blue-600">Great savings: $1,250 off</div>
                    <div className="text-xs text-gray-500 mt-1">Plan ahead smart</div>
                  </label>
                </div>

                <div className="relative">
                  <input
                    type="radio"
                    name="timeline"
                    value="flexible"
                    onChange={handleInputChange}
                    className="sr-only"
                    id="flexible"
                  />
                  <label
                    htmlFor="flexible"
                    className={`block p-4 border-2 rounded-lg cursor-pointer text-center font-medium transition-all ${
                      formData.timeline === 'flexible'
                        ? 'border-gray-500 bg-gray-50 text-gray-700'
                        : 'border-gray-300 hover:border-gray-400'
                    }`}
                  >
                    <i className="ri-search-line text-xl block mb-2"></i>
                    Just Exploring
                    <div className="text-xs text-gray-600">Still save: $1,000 off</div>
                    <div className="text-xs text-gray-500 mt-1">Future planning</div>
                  </label>
                </div>
              </div>
            </div>

            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                Project Description
              </label>
              <textarea
                name="description"
                value={formData.description}
                onChange={handleInputChange}
                maxLength={500}
                rows={4}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm resize-none"
                placeholder="Tell us about your bathroom project and when you'd like to schedule your FREE consultation..."
              ></textarea>
              <p className="text-xs text-gray-500 mt-1">
                {formData.description.length}/500 characters
              </p>
            </div>

            {submitStatus === 'success' && (
              <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                <div className="flex items-center">
                  <i className="ri-check-circle-line text-green-600 text-xl mr-3"></i>
                  <div>
                    <h4 className="font-semibold text-green-800">
                      Thank you! Your request has been submitted.
                    </h4>
                    <p className="text-green-700 text-sm">
                      A qualified contractor will contact you using your preferred method within your selected
                      timeline.
                    </p>
                  </div>
                </div>
              </div>
            )}

            {submitStatus === 'error' && (
              <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                <div className="flex items-center">
                  <i className="ri-error-warning-line text-red-600 text-xl mr-3"></i>
                  <div>
                    <h4 className="font-semibold text-red-800">Something went wrong.</h4>
                    <p className="text-red-700 text-sm">
                      Please try again later or contact us directly.
                    </p>
                  </div>
                </div>
              </div>
            )}

            <div className="pt-6">
              <Button
                type="submit"
                disabled={isSubmitting}
                size="lg"
                className="w-full text-lg bg-blue-600 hover:bg-blue-700"
              >
                {isSubmitting ? (
                  <>
                    <i className="ri-loader-4-line animate-spin mr-2"></i>
                    Processing Request...
                  </>
                ) : (
                  <>
                    <i className="ri-calculator-line mr-2"></i>
                    Get My Free Quote &amp; Pricing Info
                  </>
                )}
              </Button>

              {/* Optional informational sections – kept simple to avoid syntax errors */}
              <div className="text-center mt-4 space-y-3">
                <div className="bg-blue-100 border border-blue-200 rounded-lg p-3">
                  <p className="text-sm font-medium text-blue-800">
                    <i className="ri-lightbulb-line mr-1"></i>
                    Smart planning helps you stay on budget.
                  </p>
                </div>
                <div className="bg-green-100 border border-green-200 rounded-lg p-3">
                  <p className="text-sm font-medium text-green-800">
                    <i className="ri-hand-coin-line mr-1"></i>
                    Flexible financing options are available.
                  </p>
                </div>
                <p className="text-xs text-gray-500">
                  By submitting, you agree to our privacy policy.
                </p>
              </div>
            </div>
          </form>
        </div>
      </div>
    </section>
  );
}
